/**
 * Préférences de l'application (stockées localement, synchronisées
 * dans le cloud par la page Paramètres quand l'utilisateur est connecté).
 *
 * Tout ce qui est ici est RÉELLEMENT appliqué à l'app :
 *  - textSize        : taille du texte partout (classe sur <html>)
 *  - reduceMotion    : coupe animations et transitions
 *  - backgroundFx    : fond animé étoilé
 *  - autoplay        : la radio démarre seule à l'ouverture
 *  - defaultVolume   : volume au démarrage
 *  - haptics         : petite vibration au toucher des boutons principaux
 */

export const PREFS_KEY = 'johnnyPrefs'
export const PREFS_EVENT = 'johnny-prefs-changed'

export const DEFAULT_PREFS = Object.freeze({
  textSize: 'normal',        // 'normal' | 'large' | 'xlarge'
  reduceMotion: false,
  backgroundFx: true,
  autoplay: false,
  defaultVolume: 70,
  haptics: true,
  notifications: {
    newSongs: true,
    newEvents: true,
    newPodcasts: false,
    newsletter: true
  },
  privacy: {
    showProfile: true,
    showActivity: false,
    allowAnalytics: true
  }
})

const merge = (base, extra) => ({
  ...base,
  ...(extra || {}),
  notifications: { ...base.notifications, ...(extra?.notifications || {}) },
  privacy: { ...base.privacy, ...(extra?.privacy || {}) }
})

// Ancien format (page Paramètres v1) -> nouveau format
const fromLegacy = () => {
  try {
    const raw = localStorage.getItem('appSettings')
    if (!raw) return null
    const old = JSON.parse(raw)
    return {
      autoplay: Boolean(old?.audio?.autoplay),
      defaultVolume: Number(old?.audio?.volume ?? 70),
      reduceMotion: old?.appearance?.animations === false,
      backgroundFx: old?.appearance?.backgroundEffects !== false,
      notifications: old?.notifications,
      privacy: old?.privacy
    }
  } catch {
    return null
  }
}

export function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    if (raw) return merge(DEFAULT_PREFS, JSON.parse(raw))
  } catch { /* stockage indisponible */ }
  return merge(DEFAULT_PREFS, fromLegacy())
}

export function savePrefs(prefs) {
  const clean = merge(DEFAULT_PREFS, prefs)
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(clean))
    // Compatibilité avec le code existant (volume global, page TV)
    localStorage.setItem('audioVolume', String(clean.defaultVolume))
    localStorage.setItem('autoplay', clean.autoplay ? 'true' : 'false')
  } catch { /* rien */ }
  applyPrefs(clean)
  window.dispatchEvent(new CustomEvent(PREFS_EVENT, { detail: clean }))
  return clean
}

export function applyPrefs(prefs = loadPrefs()) {
  const root = document.documentElement
  root.classList.remove('text-large', 'text-xlarge')
  if (prefs.textSize === 'large') root.classList.add('text-large')
  if (prefs.textSize === 'xlarge') root.classList.add('text-xlarge')
  root.classList.toggle('reduce-motion', Boolean(prefs.reduceMotion))
  root.classList.toggle('no-bg-fx', !prefs.backgroundFx)
}

export function vibrate(ms = 12) {
  try {
    if (!loadPrefs().haptics) return
    navigator.vibrate?.(ms)
  } catch { /* rien */ }
}

// Version visible dans l'app (injectée par vite.config.js)
/* global __APP_VERSION__, __BUILD_STAMP__ */
export const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'
export const BUILD_STAMP = typeof __BUILD_STAMP__ !== 'undefined' ? __BUILD_STAMP__ : ''
export const VERSION_LABEL = `Version ${APP_VERSION}${BUILD_STAMP ? `, compilée le ${BUILD_STAMP}` : ''}`

try { console.info(`[100% Johnny] ${VERSION_LABEL}`) } catch { /* rien */ }
